import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import styled from 'styled-components';

import { Button } from '@components/button';
import { IconShadowContainer } from '@components/icon-shadow-container';
import { IconFormIndicatorError } from '@components/icons/form-indicator-error';
import { IconFormIndicatorSuccess } from '@components/icons/form-indicator-success';
import { Spinner } from '@components/loading-spinner';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

const errorMessageKeys: Record<string, string> = {
  missing_params: 'errorMissingParams',
  missing_code: 'errorMissingCode',
  invalid_state: 'errorInvalidState',
  expired_state: 'errorExpiredState',
  incomplete_config: 'errorIncompleteConfig',
  token_exchange_failed: 'errorTokenExchangeFailed',
  no_access_token: 'errorNoAccessToken',
  access_denied: 'errorAccessDenied',
  not_authenticated: 'errorNotAuthenticated',
};

export const OAuthCallbackPage = () => {
  const { t } = useScopedTranslate(locale);
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const success = searchParams.get('success') === 'true';
  const error = searchParams.get('error');
  const description = searchParams.get('description');

  const getErrorMessage = useCallback(
    (errorCode: string, errorDescription?: string | null) => {
      const localeKey = errorMessageKeys[errorCode] as keyof (typeof locale)['en'];
      if (localeKey) {
        return t(localeKey);
      }
      return errorDescription || t('errorUnknown');
    },
    [t],
  );

  useEffect(() => {
    const notifyParent = (result: { success: boolean; error?: string }) => {
      if (window.opener) {
        try {
          window.opener.postMessage({ type: 'oauth_callback', ...result }, window.location.origin);
        } catch {
          // Parent window communication failed
        }
      }
      try {
        localStorage.setItem(
          'oauth_callback_result',
          JSON.stringify({ ...result, timestamp: Date.now() }),
        );
      } catch {
        // localStorage not available
      }
    };

    if (success) {
      setStatus('success');
      notifyParent({ success: true });
      setTimeout(() => {
        window.close();
      }, 3500);
    } else if (error) {
      setStatus('error');
      setErrorMessage(getErrorMessage(error, description));
      notifyParent({ success: false, error });
    } else {
      setStatus('error');
      setErrorMessage(t('errorInvalidParams'));
    }
  }, [success, error, description, t, getErrorMessage]);

  const handleClose = () => {
    window.close();
  };

  if (status === 'loading') {
    return (
      <Container>
        <Stack space={4} align="center">
          <Spinner />
          <Text font="regular14" text={t('processing')} />
        </Stack>
      </Container>
    );
  }

  if (status === 'success') {
    return (
      <StatusCard
        icon={<IconFormIndicatorSuccess size={512} />}
        title={t('successTitle')}
        subtitle={t('autoClose')}
        closeText={t('close')}
        onClose={handleClose}
      />
    );
  }

  return (
    <StatusCard
      icon={<IconFormIndicatorError size={512} />}
      title={t('errorTitle')}
      subtitle={t('failedToComplete')}
      extra={errorMessage}
      closeText={t('close')}
      onClose={handleClose}
    />
  );
};

type StatusCardProps = {
  icon: ReactElement;
  title: string;
  subtitle: string;
  extra?: string;
  closeText: string;
  onClose: () => void;
};

const StatusCard = ({ icon, title, subtitle, extra, closeText, onClose }: StatusCardProps) => (
  <Container>
    <ContentWrapper>
      <BackgroundIcon>{icon}</BackgroundIcon>
      <IconShadowContainer icon={icon} containerSize={96} iconSize={48} shadowSize={140} />
      <Stack space={2} align="center">
        <Text font="bold18" text={title} />
        <CenteredText font="regular14" color="textSecondary" text={subtitle} />
      </Stack>
      {extra && <CenteredText font="regular14" color="textSecondary" text={extra} />}
      <Button variant="secondary" text={closeText} onClick={onClose} />
    </ContentWrapper>
  </Container>
);
const Container = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  width: '100%',
  padding: pxToRem(24),
  boxSizing: 'border-box',
});

const ContentWrapper = styled(Stack).attrs({
  space: 6,
  align: 'center',
})({
  position: 'relative',
  padding: pxToRem(48),
  maxWidth: pxToRem(400),
});

const BackgroundIcon = styled.div({
  position: 'absolute',
  top: pxToRem(-122),
  left: '50%',
  transform: 'translateX(-92%)',
  opacity: 0.05,
  zIndex: 0,
  pointerEvents: 'none',
});

const CenteredText = styled(Text)({
  textAlign: 'center',
  maxWidth: pxToRem(300),
});

const locale = {
  en: {
    processing: 'Processing authorization...',
    successTitle: 'Successfully connected!',
    autoClose: 'This window will close automatically',
    close: 'Close',
    errorTitle: 'Connection failed',
    failedToComplete: 'Failed to complete authorization',
    errorMissingParams: 'Missing required parameters. Please try again.',
    errorMissingCode: 'Missing authorization code. Please try again.',
    errorInvalidState: 'Invalid authorization state. Please try again.',
    errorExpiredState: 'Authorization session expired. Please try again.',
    errorIncompleteConfig: 'Integration configuration is incomplete.',
    errorTokenExchangeFailed: 'Failed to complete authorization. Please try again.',
    errorNoAccessToken: 'Authorization failed. No access token received.',
    errorAccessDenied: 'Access was denied. Please grant the required permissions.',
    errorNotAuthenticated: 'You need to be signed in to connect.',
    errorInvalidParams: 'Invalid callback parameters.',
    errorUnknown: 'An unknown error occurred.',
  },
};
